import { useState, useEffect } from 'react';
import { getChatIdParam, setChatIdParam } from './side-chat-url';
import { openSideChat } from './loomly-ai-chat-toggle';

class SideChatBridge {
  #channels = new Map();

  #getChannel(name) {
    if (!this.#channels.has(name)) {
      this.#channels.set(name, { value: undefined, listeners: new Set() });
    }
    return this.#channels.get(name);
  }

  emit(channel, value) {
    const ch = this.#getChannel(channel);
    ch.value = value;
    ch.listeners.forEach(fn => fn(value));
  }

  on(channel, callback) {
    const ch = this.#getChannel(channel);
    ch.listeners.add(callback);
    return () => ch.listeners.delete(callback);
  }

  get(channel) {
    return this.#getChannel(channel).value;
  }

  consume(channel) {
    const value = this.get(channel);
    this.clear(channel);
    return value;
  }

  clearListeners(channel) {
    if (this.#channels.has(channel)) {
      this.#channels.get(channel).listeners.clear();
    }
  }

  clear(channel) {
    this.#getChannel(channel).value = undefined;
  }

  setStreaming(active) {
    this.emit('streaming', { active });
  }

  sendPrompt(text) {
    this.emit('prompt', { text });
  }

  openChatWithPrompt(text) {
    if (this.get('streaming')?.active) return false;
    if (this.get('usageLimitHit')) {
      const $modal = $('#usage-limit-modal');
      if ($modal.length) $modal.addClass('active');
      return false;
    }
    this.setStreaming(true);
    if (!getChatIdParam()) setChatIdParam('new');
    openSideChat();
    queueMicrotask(() => this.sendPrompt(text));
    return true;
  }
}

const bridge = new SideChatBridge();
export default bridge;

export function useBridgeValue(channel, defaultValue) {
  const [value, setValue] = useState(() => bridge.get(channel) ?? defaultValue);
  useEffect(() => bridge.on(channel, setValue), [channel]);
  return value;
}

export function useAiStreaming() {
  return useBridgeValue('streaming', { active: false }).active;
}
